/**
 * Итог «Собрать из цехов» словами. Частичный сбор — источник не прочитался или часть
 * заявок не создана — не выглядит как полный: «Собрано частично: …» и что не удалось.
 * Несобранная старая просрочка по графикам кредитов и лизинга — отдельной строкой.
 * Модуль без импортов значений — проверяется node:test (`collectResult.test.mjs`).
 */
import type { CollectRequestsResult } from '@shared/api';

export type CollectTone = 'success' | 'info' | 'warning';

/** Просрочка по графику старше стольких дней не собирается — `DEBT_COLLECT_OVERDUE_DAYS` на сервере. */
export const STALE_DEBT_DAYS = 30;

/** Форма слова по числу: 1 заявка, 2 заявки, 5 заявок, 11 заявок, 21 заявка. */
export function plural(n: number, one: string, few: string, many: string): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

function requests(n: number): string {
  return `${n} ${plural(n, 'заявка', 'заявки', 'заявок')}`;
}

/** Сообщение после сбора; `money` — формат суммы экрана (`formatMoney`). */
export function collectResultMessage(
  res: CollectRequestsResult,
  money: (n: number) => string,
): { tone: CollectTone; text: string } {
  const n = res.collected_count;
  const problems: string[] = [];
  for (const s of res.sources ?? []) {
    if (s.unavailable) {
      problems.push(`• ${s.label}: источник недоступен — ${s.unavailable}`);
    } else if (s.failed > 0) {
      const reason = s.first_error ? ` — ${s.first_error}` : '';
      problems.push(`• ${s.label}: не создано ${requests(s.failed)}${reason}`);
    }
  }

  const lines: string[] = [];
  if (problems.length > 0) {
    lines.push(`Собрано частично: ${requests(n)}. Не удалось:`, ...problems);
  } else if (n > 0) {
    lines.push(`${plural(n, 'Собрана', 'Собрано', 'Собрано')} ${requests(n)}`);
  } else {
    lines.push('Новых заявок нет: всё, что подошло по сроку, уже в Фабрике платежей');
  }

  const stale = res.stale_debt_count ?? 0;
  if (stale > 0) {
    const verb = plural(stale, 'Не собран', 'Не собраны', 'Не собраны');
    const what = plural(
      stale,
      'просроченный платёж',
      'просроченных платежа',
      'просроченных платежей',
    );
    lines.push(
      `${verb} ${stale} ${what} по графику старше ${STALE_DEBT_DAYS} дней на ` +
        `${money(res.stale_debt_amount ?? 0)}. Проверьте отметки оплаты в разделе ` +
        '«Кредиты и лизинг» или создайте заявку вручную',
    );
  }

  const tone: CollectTone = problems.length > 0 ? 'warning' : stale > 0 ? 'info' : 'success';
  return { tone, text: lines.join('\n') };
}
